<script setup>
/**
 * The rehearsal thread. THE PLAN, §4 P6.
 *
 * The bar is `bar/captain-playground-dark.png`, and the single most useful thing in that
 * screenshot is what is NOT in it: nine hundred vertical pixels of empty card between a
 * one-line description and a composer, with nothing telling a first-time owner what to
 * type. Ours fills that space with the only thing that helps at that moment — three
 * opening messages the PAGE assembles from her own account's content and tops up with
 * neutral ones, at least one of them in Arabic, each one click away from being in the
 * box. Where they came from is the page's business; this component renders what it is
 * given.
 *
 * ANATOMY, and each half is chosen rather than symmetric:
 *   • The customer's message is a filled bubble aligned to the end of the line. It is the
 *     smaller half — she wrote it, she knows what it says.
 *   • The assistant's turn is a column: a marked avatar, the reply in an outlined bubble,
 *     the handover marker when it gave up, and the collapsed trace. It is the half being
 *     judged, so it is the half that gets the structure.
 *
 * `dir="auto"` sits on each bubble, not on the thread: a shop in Kuwait gets Arabic and
 * English messages in the same conversation, and a container-level direction would lay
 * one of them out backwards. A failed turn keeps the customer's message on screen —
 * losing what you typed because the network blinked is its own defect.
 */
import { ref } from 'vue';

import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

import TestTrace from './TestTrace.vue';
import { useWasloI18n } from '../i18n.js';
import { choiceLayout, tapSendsChoice } from '../helpers/test-channel.js';

defineProps({
  /**
   * `{ id, question, state: 'sending'|'ok'|'error', text, handoff, trace, error, attachment }`,
   * oldest first. Built by the page — this component renders and never fetches.
   *
   * The in-flight turn is a real entry with `state: 'sending'`, not a separate flag: the
   * customer's message and the answer to it belong to one row, and a spinner rendered
   * outside the list would sit under the wrong message the moment two are queued.
   */
  turns: { type: Array, default: () => [] },
  /** Ready-made first messages, offered only while the thread is empty. */
  starters: { type: Array, default: () => [] },
  /** True while a turn is in flight: a second tap would race the first. */
  busy: { type: Boolean, default: false },
});

const emit = defineEmits(['use-starter', 'choose', 'say', 'fetch-sample']);

const { t } = useWasloI18n();

/** Which turn's WhatsApp list is open (P-2.10): the list button opens its rows in place. */
const openList = ref(null);

/**
 * A tap on an option, as the chosen channel would send it back (P-2.10). WhatsApp and
 * Instagram carry the option's id; Messenger and Telegram send only its words, which reach
 * the assistant as a typed message.
 */
const tap = (turn, item) => {
  openList.value = null;
  if (tapSendsChoice(turn.choices?.style)) {
    emit('choose', { choiceId: item.choiceId, title: item.title });
  } else {
    emit('say', item.title);
  }
};
</script>

<template>
  <div class="flex flex-col gap-8" data-waslo-thread>
    <!-- EMPTY. Not a void with a composer under it. -->
    <div
      v-if="!turns.length"
      class="flex flex-col items-center gap-4 px-6 py-12 text-center"
      data-waslo-thread-empty
    >
      <div
        class="grid place-items-center rounded-2xl size-12 bg-n-blue-3"
        aria-hidden="true"
      >
        <span class="size-6 text-n-blue-10 i-lucide-message-circle-more" />
      </div>
      <div class="flex flex-col gap-1">
        <h2 class="mb-0 text-lg font-medium text-n-slate-12">
          {{ t('WASLO.TEST.THREAD.EMPTY_TITLE') }}
        </h2>
        <p class="mb-0 text-sm leading-6 max-w-[52ch] text-n-slate-11">
          {{ t('WASLO.TEST.THREAD.EMPTY_BODY') }}
        </p>
      </div>

      <div v-if="starters.length" class="flex flex-col items-center gap-3 mt-2">
        <span class="text-sm text-n-slate-10">
          {{ t('WASLO.TEST.THREAD.STARTERS') }}
        </span>
        <div class="flex flex-wrap justify-center gap-2">
          <button
            v-for="starter in starters"
            :key="starter"
            type="button"
            dir="auto"
            class="px-3 py-2 text-sm outline outline-1 rounded-xl max-w-[36ch] outline-n-weak bg-n-solid-1 text-n-slate-11 hover:bg-n-alpha-1 hover:text-n-slate-12"
            data-waslo-thread-starter
            @click="emit('use-starter', starter)"
          >
            {{ starter }}
          </button>
        </div>
      </div>
    </div>

    <!-- THE TURNS. -->
    <div
      v-for="turn in turns"
      :key="turn.id"
      class="flex flex-col gap-3"
      data-waslo-turn
    >
      <div class="flex justify-end">
        <p
          dir="auto"
          class="mb-0 px-4 py-2.5 text-sm leading-6 whitespace-pre-wrap rounded-2xl max-w-[42rem] bg-n-solid-3 text-n-slate-12"
          data-waslo-turn-question
        >
          {{ turn.question }}
        </p>
      </div>

      <div class="flex items-start gap-3">
        <div
          class="grid place-items-center rounded-xl shrink-0 size-8 bg-n-blue-3"
          aria-hidden="true"
        >
          <span class="size-4 text-n-blue-10 i-lucide-bot-message-square" />
        </div>

        <div class="flex flex-col flex-1 min-w-0 gap-2">
          <!-- In flight. -->
          <div
            v-if="turn.state === 'sending'"
            class="flex items-center gap-2 py-2 text-sm text-n-slate-11"
            data-waslo-turn-sending
          >
            <Spinner class="size-4" />
            <span>{{ t('WASLO.TEST.THREAD.WRITING') }}</span>
          </div>

          <!-- Refused. The customer's message above is deliberately still on screen. -->
          <template v-else-if="turn.state === 'error'">
            <div
              class="flex flex-col gap-1 px-4 py-3 outline outline-1 rounded-2xl outline-n-ruby-8 bg-n-solid-1"
              role="alert"
              data-waslo-turn-error
            >
              <p class="mb-0 text-sm leading-6 text-n-ruby-11" dir="auto">
                {{ turn.error }}
              </p>
              <p class="mb-0 text-sm text-n-slate-11">
                {{ t('WASLO.TEST.THREAD.FAILED') }}
              </p>
            </div>
          </template>

          <template v-else>
            <p
              v-if="turn.text"
              dir="auto"
              class="px-4 py-3 mb-0 text-sm leading-6 whitespace-pre-wrap outline outline-1 rounded-2xl outline-n-weak bg-n-solid-1 text-n-slate-12"
              data-waslo-turn-reply
            >
              {{ turn.text }}
            </p>
            <p
              v-else
              class="px-4 py-3 mb-0 text-sm leading-6 outline outline-1 rounded-2xl outline-n-weak bg-n-solid-1 text-n-slate-11"
              data-waslo-turn-reply
            >
              {{ t('WASLO.TEST.THREAD.NO_REPLY') }}
            </p>

            <!-- Amber, not ruby: handing over is the assistant working as designed, and
                 the queue on Overview is where it becomes something to act on. -->
            <p
              v-if="turn.handoff"
              class="flex items-center gap-2 mb-0 text-sm text-n-amber-11"
              data-waslo-turn-handoff
            >
              <span class="shrink-0 size-4 i-lucide-user-round-check" />
              <span>{{ t('WASLO.TEST.THREAD.HANDOFF') }}</span>
            </p>

            <!-- P-4.1 / P-4.2: what the chosen channel sends under the words. A link written into
                 the text (any channel but WhatsApp) is already in the bubble, so nothing is drawn. -->
            <div
              v-if="turn.attachment?.kind === 'link'"
              class="flex flex-col items-start gap-1"
              data-waslo-turn-link
            >
              <span
                class="inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded-lg outline outline-1 -outline-offset-1 outline-n-weak bg-n-alpha-1 text-n-blue-11"
              >
                <span class="shrink-0 size-4 i-lucide-external-link" />
                <bdi>{{ turn.attachment.label }}</bdi>
              </span>
              <span class="text-xs break-all text-n-slate-10" dir="ltr">
                {{ turn.attachment.url }}
              </span>
            </div>
            <div
              v-else-if="turn.attachment?.kind === 'location'"
              class="flex items-start gap-2 px-3 py-2 outline outline-1 -outline-offset-1 rounded-lg outline-n-weak bg-n-solid-1"
              data-waslo-turn-location
            >
              <span
                class="shrink-0 mt-0.5 size-4 i-lucide-map-pin text-n-slate-11"
              />
              <span class="flex flex-col min-w-0">
                <bdi class="text-sm text-n-slate-12">{{
                  turn.attachment.name
                }}</bdi>
                <bdi
                  v-if="turn.attachment.address"
                  class="text-xs text-n-slate-11"
                >
                  {{ turn.attachment.address }}
                </bdi>
              </span>
            </div>

            <!-- P-2.10: the options, as the chosen channel shows them. A numbered list is
                 already in the reply's text, so it draws nothing here. -->
            <div
              v-if="choiceLayout(turn.choices).kind !== 'none'"
              class="flex flex-col gap-2"
              :data-waslo-turn-choices="choiceLayout(turn.choices).kind"
            >
              <button
                v-if="choiceLayout(turn.choices).kind === 'list'"
                type="button"
                class="flex items-center self-start gap-2 px-3 py-1.5 text-sm rounded-lg outline outline-1 outline-n-weak bg-n-solid-1 text-n-blue-11"
                :disabled="busy"
                data-waslo-turn-list-button
                @click="openList = openList === turn.id ? null : turn.id"
              >
                <span class="size-4 i-lucide-list" aria-hidden="true" />
                <bdi>{{ choiceLayout(turn.choices).listButton }}</bdi>
              </button>
              <div
                v-if="
                  choiceLayout(turn.choices).kind !== 'list' ||
                  openList === turn.id
                "
                class="flex gap-2"
                :class="
                  choiceLayout(turn.choices).kind === 'list'
                    ? 'flex-col items-start'
                    : 'flex-wrap'
                "
              >
                <button
                  v-for="item in choiceLayout(turn.choices).items"
                  :key="item.choiceId"
                  type="button"
                  dir="auto"
                  class="px-3 py-1.5 text-sm outline outline-1 bg-n-solid-1 text-n-blue-11 hover:bg-n-alpha-1 disabled:opacity-50"
                  :class="[
                    choiceLayout(turn.choices).kind === 'chips'
                      ? 'rounded-full'
                      : 'rounded-lg',
                    item.fallback
                      ? 'outline-dashed outline-n-amber-8'
                      : 'outline-n-weak',
                  ]"
                  :title="item.fallback ? t('WASLO.TEST.THREAD.FALLBACK') : ''"
                  :disabled="busy"
                  :data-waslo-turn-choice="item.choiceId"
                  :data-waslo-turn-choice-fallback="item.fallback || null"
                  @click="tap(turn, item)"
                >
                  {{ item.title }}
                </button>
              </div>
              <p
                v-if="turn.choices.items.some(item => item.fallback)"
                class="mb-0 text-xs text-n-amber-11"
                data-waslo-turn-choices-fallback
              >
                {{ t('WASLO.TEST.THREAD.FALLBACK') }}
              </p>
            </div>

            <!-- The menu the assistant put under its reply, from a BFF older than P-2.10 (no
                 `choices` key at all). A newer BFF sends the menu as `choices` too, so on a
                 numbered channel this stays empty: the list is already in the reply's text.
                 A tap answers the way a customer would: the title is the next message and the
                 id rides alongside it, exactly as a WhatsApp tap carries it. -->
            <div
              v-else-if="turn.menu && !turn.choices"
              class="flex flex-col gap-2"
              data-waslo-turn-menu
            >
              <div class="flex flex-wrap gap-2">
                <button
                  v-for="option in turn.menu.options"
                  :key="option.choiceId"
                  type="button"
                  dir="auto"
                  class="px-3 py-1.5 text-sm rounded-full outline outline-1 outline-n-weak bg-n-solid-1 text-n-blue-11 hover:bg-n-alpha-1 disabled:opacity-50"
                  :disabled="busy"
                  data-waslo-turn-menu-option
                  @click="emit('choose', option)"
                >
                  {{ option.title }}
                </button>
              </div>
              <p class="mb-0 text-xs text-n-slate-10">
                {{ t('WASLO.TEST.THREAD.MENU_TAP') }}
              </p>
            </div>

            <TestTrace
              v-if="turn.trace"
              :trace="turn.trace"
              :menu="turn.menu ?? null"
              @fetch-sample="id => emit('fetch-sample', id)"
            />
          </template>
        </div>
      </div>
    </div>
  </div>
</template>
